import { useEffect, useRef, useState } from 'react'
import { api } from './api/client'
import type { Me, Project } from './types'
import Header, { type Tab } from './components/Header'
import NewProjectModal from './components/NewProjectModal'
import InviteClientModal from './components/InviteClientModal'
import ClientApp from './ClientApp'
import ScheduleTab from './tabs/ScheduleTab'
import DecisionsTab from './tabs/DecisionsTab'
import BudgetTab from './tabs/BudgetTab'
import ChangeOrdersTab from './tabs/ChangeOrdersTab'
import DailyLogTab from './tabs/DailyLogTab'
import InspectionsTab from './tabs/InspectionsTab'
import MaterialsTab from './tabs/MaterialsTab'
import DrawsTab from './tabs/DrawsTab'
import PunchTab from './tabs/PunchTab'
import SubsTab from './tabs/SubsTab'
import SelectionsTab from './tabs/SelectionsTab'
import ScopeTab from './tabs/ScopeTab'
import SiteWalkTab from './tabs/SiteWalkTab'
import PlanVersionsTab from './tabs/PlanVersionsTab'
import SpecBookTab from './tabs/SpecBookTab'
import DigestTab from './tabs/DigestTab'
import WarrantyTab from './tabs/WarrantyTab'
import TodayTab from './tabs/TodayTab'
import ComplianceTab from './tabs/ComplianceTab'
import BidsTab from './tabs/BidsTab'
import LessonsTab from './tabs/LessonsTab'
import FinancialsTab from './tabs/FinancialsTab'
import InsightsTab from './tabs/InsightsTab'
import SettingsTab from './tabs/SettingsTab'
import InboxTab from './tabs/InboxTab'
import CommsTab from './tabs/CommsTab'
import LeadInboxTab from './tabs/LeadInboxTab'
import LotsTab from './tabs/LotsTab'
import { NAV_TAB_CHANGE_EVENT } from './lib/nav-target'
import TabNav from './components/TabNav'
import {
  TAB_GROUPS,
  ALL_GROUPED_TABS,
  getGroupForTab,
  getDefaultTabForGroup,
  type GroupId,
} from './lib/tab-groups'

const TAB_KEY = 'heritage_active_tab'
const PROJECT_KEY = 'heritage_active_project'
const GROUP_KEY = 'heritage_active_group'
const TAB_PER_GROUP_KEY = 'heritage_active_tab_per_group'

// Workflow-stage URL: /admin/<group>/<tab>, with /admin/today as the
// special-case path for the standalone Today tab. parseUrl returns the
// state implied by window.location, including legacy ?tab=foo redirects.
function parseUrl():
  | { kind: 'today' }
  | { kind: 'group_tab'; group: GroupId; tab: Tab }
  | { kind: 'legacy_query'; tab: Tab }
  | { kind: 'unknown' } {
  const path = window.location.pathname
  const m = path.match(/^\/admin\/([^/]+)(?:\/([^/]+))?\/?$/)
  if (m) {
    const seg1 = m[1]
    const seg2 = m[2]
    if (seg1 === 'today' && !seg2) return { kind: 'today' }
    const groupDef = TAB_GROUPS.find((g) => g.id === seg1)
    if (groupDef) {
      const tab = (seg2 as Tab | undefined) ?? groupDef.tabs[0].id
      if (groupDef.tabs.some((t) => t.id === tab)) {
        return { kind: 'group_tab', group: groupDef.id, tab }
      }
    }
    // Old single-segment URL like /admin/site-walks (pre-grouping). If seg1
    // is a known leaf tab, surface as legacy so the URL sync redirects to
    // the new /admin/<group>/<tab> path on next render.
    if (!seg2 && ALL_GROUPED_TABS.has(seg1 as Tab)) {
      return { kind: 'legacy_query', tab: seg1 as Tab }
    }
  }
  // Legacy: ?tab=foo (used by OAuth callback redirects + bookmarks)
  const qp = new URLSearchParams(window.location.search).get('tab')
  if (qp) return { kind: 'legacy_query', tab: qp as Tab }
  return { kind: 'unknown' }
}

function urlForTab(tab: Tab): string {
  if (tab === 'today') return '/admin/today'
  const g = getGroupForTab(tab)
  if (!g) return '/admin/today'
  return `/admin/${g}/${tab}`
}

function readTabPerGroup(): Partial<Record<GroupId, Tab>> {
  try {
    const raw = localStorage.getItem(TAB_PER_GROUP_KEY)
    return raw ? (JSON.parse(raw) as Partial<Record<GroupId, Tab>>) : {}
  } catch {
    return {}
  }
}

function writeTabPerGroup(map: Partial<Record<GroupId, Tab>>): void {
  localStorage.setItem(TAB_PER_GROUP_KEY, JSON.stringify(map))
}

export default function App() {
  const [projects, setProjects] = useState<Project[]>([])
  const [activeProjectId, setActiveProjectId] = useState<string | null>(
    localStorage.getItem(PROJECT_KEY)
  )
  const [tab, setTab] = useState<Tab>(() => {
    // URL > legacy ?tab= > localStorage > default 'today'
    const parsed = parseUrl()
    if (parsed.kind === 'today') return 'today'
    if (parsed.kind === 'group_tab') return parsed.tab
    if (parsed.kind === 'legacy_query') return parsed.tab
    return (localStorage.getItem(TAB_KEY) as Tab) ?? 'today'
  })
  const [activeGroup, setActiveGroup] = useState<GroupId | 'today'>(() => {
    const parsed = parseUrl()
    if (parsed.kind === 'today') return 'today'
    if (parsed.kind === 'group_tab') return parsed.group
    if (parsed.kind === 'legacy_query') {
      const g = getGroupForTab(parsed.tab)
      return g ?? 'today'
    }
    const saved = localStorage.getItem(GROUP_KEY)
    if (saved === 'today' || TAB_GROUPS.some((x) => x.id === saved)) {
      return saved as GroupId | 'today'
    }
    const t = (localStorage.getItem(TAB_KEY) as Tab) ?? 'today'
    return getGroupForTab(t) ?? 'today'
  })
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [newProjectOpen, setNewProjectOpen] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [me, setMe] = useState<Me | null>(null)

  useEffect(() => {
    // Identify the user first; admins get the full app, clients get ClientApp.
    api
      .me()
      .then((d) => {
        setMe(d)
        if (d.role === 'admin') {
          return api.projects.list().then((list) => {
            setProjects(list)
            if (!activeProjectId && list.length > 0) {
              setActiveProjectId(list[0].id)
              localStorage.setItem(PROJECT_KEY, list[0].id)
            }
          })
        }
        return undefined
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (activeProjectId) localStorage.setItem(PROJECT_KEY, activeProjectId)
  }, [activeProjectId])

  useEffect(() => {
    localStorage.setItem(TAB_KEY, tab)
  }, [tab])
  useEffect(() => {
    localStorage.setItem(GROUP_KEY, activeGroup)
  }, [activeGroup])

  // Sync URL whenever tab changes. Use replaceState on initial mount when
  // legacy ?tab=foo was present (so back button doesn't bounce there);
  // pushState otherwise so back/forward step through tab history.
  const initialUrlKindRef = useRef(parseUrl().kind)
  const didInitialReplaceRef = useRef(false)
  useEffect(() => {
    const target = urlForTab(tab)
    if (window.location.pathname + window.location.search === target) return
    if (
      !didInitialReplaceRef.current &&
      (initialUrlKindRef.current === 'legacy_query' ||
        initialUrlKindRef.current === 'unknown')
    ) {
      window.history.replaceState({}, '', target)
      didInitialReplaceRef.current = true
    } else {
      window.history.pushState({}, '', target)
    }
  }, [tab])

  // popstate (back/forward): re-derive tab + group from URL.
  useEffect(() => {
    function onPop() {
      const parsed = parseUrl()
      if (parsed.kind === 'today') {
        setTab('today')
        setActiveGroup('today')
      } else if (parsed.kind === 'group_tab') {
        setTab(parsed.tab)
        setActiveGroup(parsed.group)
      }
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // Cross-tab navigation: sibling tabs (e.g. ScopeTab linking to SpecBookTab
  // or PlanVersionsTab) dispatch this event with the target tab name.
  useEffect(() => {
    function onNav(e: Event) {
      const detail = (e as CustomEvent).detail as { tab?: string }
      if (detail?.tab) {
        const newTab = detail.tab as Tab
        setTab(newTab)
        const g = getGroupForTab(newTab)
        setActiveGroup(g ?? 'today')
      }
    }
    window.addEventListener(NAV_TAB_CHANGE_EVENT, onNav)
    return () => window.removeEventListener(NAV_TAB_CHANGE_EVENT, onNav)
  }, [])

  function pickGroup(g: GroupId | 'today') {
    setActiveGroup(g)
    if (g === 'today') {
      setTab('today')
      return
    }
    // Restore the last-visited tab in this group, or default to first.
    const remembered = readTabPerGroup()[g]
    const next =
      remembered && ALL_GROUPED_TABS.has(remembered)
        ? remembered
        : getDefaultTabForGroup(g)
    setTab(next)
  }
  function pickTab(t: Tab) {
    setTab(t)
    if (t === 'today') {
      setActiveGroup('today')
      return
    }
    const g = getGroupForTab(t)
    if (g) {
      setActiveGroup(g)
      const map = readTabPerGroup()
      map[g] = t
      writeTabPerGroup(map)
    }
  }

  const project = projects.find((p) => p.id === activeProjectId)

  async function exportProject() {
    if (!project) return
    const res = await api.projects.export(project.id)
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${project.name.replace(/[^a-z0-9]+/gi, '_')}_${new Date()
      .toISOString()
      .slice(0, 10)}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (error) {
    return (
      <main className="shell">
        <h1>Heritage Command Center</h1>
        <div className="card alert">
          <strong>API error:</strong> {error}
          <p className="muted small mt-2">API URL: {api.apiUrl}</p>
        </div>
      </main>
    )
  }

  if (loading) {
    return (
      <main className="shell">
        <p className="muted">Loading…</p>
      </main>
    )
  }

  // Client users see the restricted portal layout.
  if (me && me.role !== 'admin') {
    return <ClientApp me={me} />
  }

  if (!project) {
    return (
      <>
        <main className="shell">
          <h1>Heritage Command Center</h1>
          <p className="muted">No projects yet.</p>
          <button className="btn-primary mt-2" onClick={() => setNewProjectOpen(true)}>
            + Create first project
          </button>
        </main>
        {newProjectOpen && (
          <NewProjectModal
            onClose={() => setNewProjectOpen(false)}
            onCreated={(p) => {
              setProjects((prev) => [...prev, p])
              setActiveProjectId(p.id)
              setNewProjectOpen(false)
            }}
          />
        )}
      </>
    )
  }

  return (
    <>
      <Header
        projects={projects}
        activeProjectId={activeProjectId}
        onProjectChange={setActiveProjectId}
        onExport={exportProject}
        onNewProject={() => setNewProjectOpen(true)}
        onInviteClient={() => setInviteOpen(true)}
      />
      <TabNav
        activeGroup={activeGroup}
        activeTab={tab}
        onPickGroup={pickGroup}
        onPickTab={pickTab}
      />
      {newProjectOpen && (
        <NewProjectModal
          onClose={() => setNewProjectOpen(false)}
          onCreated={(p) => {
            setProjects((prev) => [...prev, p])
            setActiveProjectId(p.id)
            setNewProjectOpen(false)
          }}
        />
      )}
      {inviteOpen && project && (
        <InviteClientModal
          project={project}
          onClose={() => setInviteOpen(false)}
          onUpdated={(p) => {
            setProjects((prev) => prev.map((x) => (x.id === p.id ? p : x)))
          }}
        />
      )}
      <main className="main">
        {tab === 'schedule' && <ScheduleTab project={project} />}
        {tab === 'decisions' && <DecisionsTab project={project} />}
        {tab === 'budget' && <BudgetTab project={project} />}
        {tab === 'change-orders' && <ChangeOrdersTab project={project} />}
        {tab === 'daily-log' && <DailyLogTab project={project} />}
        {tab === 'inspections' && <InspectionsTab project={project} />}
        {tab === 'materials' && <MaterialsTab project={project} />}
        {tab === 'draws' && <DrawsTab project={project} />}
        {tab === 'punch' && <PunchTab project={project} />}
        {tab === 'subs' && <SubsTab />}
        {tab === 'selections' && <SelectionsTab project={project} />}
        {tab === 'scope' && <ScopeTab project={project} />}
        {tab === 'site-walks' && <SiteWalkTab project={project} />}
        {tab === 'plans' && <PlanVersionsTab project={project} />}
        {tab === 'spec' && <SpecBookTab />}
        {tab === 'digest' && <DigestTab project={project} />}
        {tab === 'warranty' && <WarrantyTab project={project} />}
        {tab === 'today' && <TodayTab />}
        {tab === 'compliance' && <ComplianceTab />}
        {tab === 'bids' && <BidsTab project={project} />}
        {tab === 'lessons' && <LessonsTab project={project} />}
        {tab === 'financials' && <FinancialsTab project={project} />}
        {tab === 'insights' && <InsightsTab project={project} />}
        {tab === 'inbox' && <InboxTab />}
        {tab === 'comms' && <CommsTab />}
        {tab === 'lots' && <LotsTab />}
        {tab === 'lead-inbox' && <LeadInboxTab />}
        {tab === 'settings' && (
          <SettingsTab
            project={project}
            onProjectUpdated={(p) =>
              setProjects((prev) => prev.map((x) => (x.id === p.id ? p : x)))
            }
          />
        )}
      </main>
    </>
  )
}
